import React, { useState, useEffect } from 'react';
import {
  Wrench, Plus, Trash2, Sparkles, Copy, Search, X, Check, Edit2, Save,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ToolsLibrary() {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [packMode, setPackMode] = useState({}); // { [checklistId]: Set<toolIndex> of checked items }
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [editingName, setEditingName] = useState(null);

  useEffect(() => { fetchChecklists(); }, []);

  const fetchChecklists = async () => {
    try {
      const res = await fetch(`${API}/tools`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setChecklists(data);
        if (data.length && !activeId) setActiveId(data[0].checklist_id);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const active = checklists.find(c => c.checklist_id === activeId);
  const filtered = checklists.filter(c =>
    !search || c.job_type.toLowerCase().includes(search.toLowerCase())
  );

  const createBlank = async () => {
    try {
      const res = await fetch(`${API}/tools`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ job_type: 'New Checklist', description: '', tools: [] }),
      });
      if (res.ok) {
        const c = await res.json();
        setChecklists(prev => [...prev, c]);
        setActiveId(c.checklist_id);
        setEditingName(c.checklist_id);
      }
    } catch { toast.error('Failed to create'); }
  };

  const updateChecklist = async (id, patch) => {
    try {
      const res = await fetch(`${API}/tools/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
      if (res.ok) {
        const updated = await res.json();
        setChecklists(prev => prev.map(c => c.checklist_id === id ? updated : c));
      }
    } catch { toast.error('Failed to update'); }
  };

  const removeChecklist = async (id) => {
    if (!confirm('Delete this checklist?')) return;
    try {
      await fetch(`${API}/tools/${id}`, { method: 'DELETE', credentials: 'include' });
      setChecklists(prev => prev.filter(c => c.checklist_id !== id));
      if (activeId === id) setActiveId(checklists[0]?.checklist_id || null);
    } catch { toast.error('Failed to delete'); }
  };

  const addTool = () => {
    if (!active) return;
    updateChecklist(active.checklist_id, { tools: [...active.tools, ''] });
  };

  const updateTool = (idx, value) => {
    if (!active) return;
    const newTools = [...active.tools];
    newTools[idx] = value;
    // Optimistic local state to avoid lag
    setChecklists(prev => prev.map(c =>
      c.checklist_id === active.checklist_id ? { ...c, tools: newTools } : c
    ));
  };

  const commitTool = () => {
    if (!active) return;
    updateChecklist(active.checklist_id, { tools: active.tools });
  };

  const removeTool = (idx) => {
    if (!active) return;
    const newTools = active.tools.filter((_, i) => i !== idx);
    updateChecklist(active.checklist_id, { tools: newTools });
  };

  const togglePacked = (idx) => {
    if (!active) return;
    setPackMode(prev => {
      const set = new Set(prev[active.checklist_id] || []);
      if (set.has(idx)) set.delete(idx); else set.add(idx);
      return { ...prev, [active.checklist_id]: set };
    });
  };

  const resetPacked = () => {
    if (!active) return;
    setPackMode(prev => ({ ...prev, [active.checklist_id]: new Set() }));
  };

  const copyListToClipboard = async () => {
    if (!active) return;
    const txt = [`${active.job_type} — packing list`, '', ...active.tools.map(t => `[ ] ${t}`)].join('\n');
    try {
      await navigator.clipboard.writeText(txt);
      toast.success('Copied to clipboard');
    } catch { toast.error('Clipboard unavailable'); }
  };

  const runAiSuggest = async () => {
    if (!aiInput.trim()) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/tools/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ job_description: aiInput }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'AI failed');
        return;
      }
      const suggestion = await res.json();
      // Save as a new checklist
      const createRes = await fetch(`${API}/tools`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          job_type: suggestion.job_type || 'AI Suggestion',
          description: suggestion.description || '',
          tools: suggestion.tools || [],
        }),
      });
      if (createRes.ok) {
        const newChecklist = await createRes.json();
        setChecklists(prev => [...prev, newChecklist]);
        setActiveId(newChecklist.checklist_id);
        setShowAiDialog(false);
        setAiInput('');
        toast.success('AI checklist added');
      }
    } catch { toast.error('AI request failed'); }
    finally { setAiLoading(false); }
  };

  const packedCount = active ? (packMode[active.checklist_id]?.size || 0) : 0;
  const packedTotal = active?.tools.length || 0;

  return (
    <div className="animate-fade-in" data-testid="tools-library">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Tools Library
          </h1>
          <p className="text-sm text-[#6B7A73] mt-1">
            Packing checklists by job type · AI suggests tools from a job description
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowAiDialog(true)}
            className="bg-[#2D6B68] text-white hover:bg-[#235552] rounded-md"
            data-testid="ai-suggest-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" /> AI Suggest
          </Button>
          <Button
            onClick={createBlank}
            variant="outline"
            className="border-[#E6EBE8] text-[#1A1D1C] hover:bg-[#F5F6F5] rounded-md"
            data-testid="new-checklist-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Checklist
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* LEFT — list */}
        <div className="lg:col-span-1">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7A73]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search job type..."
              className="pl-9 bg-white border-[#E6EBE8]"
              data-testid="tools-search"
            />
          </div>
          <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden max-h-[calc(100vh-280px)] overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center">
                <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="p-6 text-sm text-[#6B7A73] text-center">No checklists yet.</p>
            ) : (
              <ul className="divide-y divide-[#F0F3F1]">
                {filtered.map(c => (
                  <li key={c.checklist_id}>
                    <button
                      onClick={() => setActiveId(c.checklist_id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        activeId === c.checklist_id ? 'bg-[#E5F0EE]' : 'hover:bg-[#F9F9F8]'
                      }`}
                      data-testid={`checklist-item-${c.checklist_id}`}
                    >
                      <p className="text-sm font-medium text-[#1A1D1C]">{c.job_type}</p>
                      <p className="text-[10px] text-[#6B7A73] mt-0.5">
                        {c.tools.length} {c.tools.length === 1 ? 'item' : 'items'}
                        {c.is_seeded && <span className="ml-2 px-1 py-0.5 bg-[#F0F3F1] rounded text-[9px] uppercase">Preset</span>}
                        {c.estimate_number && (
                          <span className="ml-2 px-1 py-0.5 bg-[#E5F0EE] text-[#2D6B68] rounded text-[9px] uppercase tabular-nums"
                                style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                            {c.estimate_number}
                          </span>
                        )}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* RIGHT — detail */}
        <div className="lg:col-span-3">
          {active ? (
            <div className="bg-white border border-[#E6EBE8] rounded-md p-5 space-y-4" data-testid="active-checklist">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  {editingName === active.checklist_id ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={active.job_type}
                        onChange={e => setChecklists(prev => prev.map(c =>
                          c.checklist_id === active.checklist_id ? { ...c, job_type: e.target.value } : c
                        ))}
                        className="font-semibold text-lg border-[#E6EBE8]"
                        data-testid="edit-job-type-input"
                      />
                      <Button
                        onClick={() => { updateChecklist(active.checklist_id, { job_type: active.job_type }); setEditingName(null); }}
                        size="sm"
                        className="bg-[#2D6B68] text-white hover:bg-[#235552]"
                      >
                        <Save className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-[#2D6B68]" />
                      <h2 className="text-lg font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
                        {active.job_type}
                      </h2>
                      <button
                        onClick={() => setEditingName(active.checklist_id)}
                        className="text-[#6B7A73] hover:text-[#1A1D1C]"
                        data-testid="edit-name-btn"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <Textarea
                    value={active.description || ''}
                    onChange={e => setChecklists(prev => prev.map(c =>
                      c.checklist_id === active.checklist_id ? { ...c, description: e.target.value } : c
                    ))}
                    onBlur={() => updateChecklist(active.checklist_id, { description: active.description })}
                    placeholder="Describe what this checklist is for..."
                    rows={2}
                    className="mt-2 text-sm bg-white border-[#E6EBE8] resize-none"
                    data-testid="checklist-description-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={copyListToClipboard} variant="outline" size="sm" className="border-[#E6EBE8]" data-testid="copy-list-btn">
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                  </Button>
                  <Button onClick={() => removeChecklist(active.checklist_id)} variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>

              {/* Pack progress */}
              {packedTotal > 0 && (
                <div className="flex items-center gap-3 bg-[#F9F9F8] border border-[#E6EBE8] rounded-md px-3 py-2">
                  <div className="flex-1">
                    <div className="h-1.5 bg-[#E6EBE8] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#2D6B68] transition-all"
                        style={{ width: `${(packedCount / packedTotal) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-[#6B7A73] font-medium tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    {packedCount} / {packedTotal} packed
                  </span>
                  <button
                    onClick={resetPacked}
                    className="text-[10px] uppercase tracking-wider text-[#6B7A73] hover:text-[#1A1D1C]"
                    data-testid="reset-pack-btn"
                  >
                    Reset
                  </button>
                </div>
              )}

              {/* Tools list */}
              <div className="space-y-1.5">
                {active.tools.map((tool, idx) => {
                  const packed = packMode[active.checklist_id]?.has(idx);
                  return (
                    <div key={idx} className="flex items-center gap-2 group" data-testid={`tool-row-${idx}`}>
                      <button
                        onClick={() => togglePacked(idx)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          packed
                            ? 'bg-[#2D6B68] border-[#2D6B68] text-white'
                            : 'bg-white border-[#E6EBE8] hover:border-[#2D6B68]'
                        }`}
                        data-testid={`tool-check-${idx}`}
                      >
                        {packed && <Check className="w-3 h-3" />}
                      </button>
                      <Input
                        value={tool}
                        onChange={e => updateTool(idx, e.target.value)}
                        onBlur={commitTool}
                        placeholder="Tool name..."
                        className={`flex-1 h-8 text-sm border-[#E6EBE8] bg-white ${
                          packed ? 'line-through text-[#6B7A73]' : 'text-[#1A1D1C]'
                        }`}
                        data-testid={`tool-input-${idx}`}
                      />
                      <button
                        onClick={() => removeTool(idx)}
                        className="p-1 text-[#6B7A73] hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  );
                })}
                <Button
                  onClick={addTool}
                  variant="ghost"
                  size="sm"
                  className="text-[#2D6B68] hover:bg-[#F0F3F1] mt-2"
                  data-testid="add-tool-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add tool
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#E6EBE8] rounded-md p-12 text-center">
              <Wrench className="w-10 h-10 text-[#E6EBE8] mx-auto mb-3" />
              <p className="text-sm text-[#6B7A73]">Select or create a checklist to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* AI Suggest dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="bg-white rounded-md border border-[#E6EBE8] max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
              <Sparkles className="w-4 h-4 text-[#2D6B68]" />
              AI Tool Suggestion
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-xs text-[#6B7A73]">Describe the job</label>
            <Textarea
              value={aiInput}
              onChange={e => setAiInput(e.target.value)}
              placeholder="e.g. Install a 10x12 deck with composite decking and aluminum railings on an existing second-story base"
              rows={4}
              className="bg-white border-[#E6EBE8] resize-none"
              data-testid="ai-input"
            />
            <p className="text-xs text-[#6B7A73]">
              Claude will return a practical packing list (15-25 items) — saved as a new checklist you can edit.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAiDialog(false)}
              className="border-[#E6EBE8]"
              disabled={aiLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={runAiSuggest}
              disabled={aiLoading || !aiInput.trim()}
              className="bg-[#2D6B68] text-white hover:bg-[#235552]"
              data-testid="ai-run-btn"
            >
              {aiLoading ? 'Generating…' : 'Suggest Tools'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
